import { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { Pencil, Plus, Trash } from 'lucide-react';
import { getBorrowerPairs } from '../../services/borrowerApi';
import AddEditBorrowerPairSection from './AddEditBorrowerPairSection';
import ConfirmDeleteBorrowerModal from './ConfirmDeleteBorrowerModal';
import FloatingPopup from './FloatingPopup';

const BorrowerPairSection = forwardRef(({
    token,
    loanid,
    onSelect,
    showToast,
    setToastText,
    showWarningToast,
    setWarningMessage,
    isReadOnly,
    setLoading
}, ref) => {
    const [borrowerPairs, setBorrowerPairs] = useState([]);
    const [selectedPairId, setSelectedPairId] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [pairToEdit, setPairToEdit] = useState(null);
    const [showEditSection, setShowEditSection] = useState(false);

    const loadPairs = async (retainSelectedLegacyId = null) => {
        try {
            const result = await getBorrowerPairs(token, loanid);
            setBorrowerPairs(result);

            const idToKeep = retainSelectedLegacyId || selectedPairId;

            if (idToKeep && result.some(p => p.legacyId === idToKeep)) {
                const selected = result.find(p => p.legacyId === idToKeep);
                setSelectedPairId(selected.legacyId);
                onSelect(selected.id, result.indexOf(selected) + 1, selected.legacyId);
            }
            else if (result.length > 0) {
                setSelectedPairId(result[0].legacyId);
                onSelect(result[0].id, 1, result[0].legacyId);
            }
            else {
                setSelectedPairId('');
                onSelect('', null, '');
            }

        } catch (err) {
            setWarningMessage("Failed to load borrower pairs.");
            showWarningToast(true);
        }
    };

    useImperativeHandle(ref, () => ({
        reload: (retainId) => loadPairs(retainId)
    }));


    useEffect(() => {
        if (token && loanid) loadPairs(selectedPairId);
    }, [token, loanid]);

    const formatLabel = (pair) => {
        return pair.coborrower?.firstName ?
            `${pair.borrower.fullName} & ${pair.coborrower.fullName}` :
            pair.borrower.fullName;
    };

    const handleAdd = () => {
        setEditMode(false);
        setPairToEdit(null);
        setShowEditSection(true);
    };

    const handleEdit = () => {
        const selected = borrowerPairs.find(p => p.legacyId === selectedPairId);
        setEditMode(true);
        setPairToEdit(selected);
        setShowEditSection(true);
    };


    const handleDelete = () => {
        setShowDeleteModal(true);
    };

    return (
        <div className="pt-0 pr-4 pb-4 rounded-lg w-full max-w-xl">
            <label className="form-label text-sm mb-1 block">Select Borrower Pair</label>
            <div className="flex items-center gap-3">
                <select
                    id="borrower-pair"
                    className="form-input text-sm border rounded px-2 py-1 w-full max-w-2xl"
                    value={selectedPairId}
                    onChange={(e) => {
                        const selectedLegacyId = e.target.value;
                        setSelectedPairId(selectedLegacyId);

                        const selected = borrowerPairs.find(p => p.legacyId === selectedLegacyId);
                        const index = borrowerPairs.indexOf(selected) + 1;

                        onSelect(selected?.id, index, selected?.legacyId);
                    }}

                >
                    {borrowerPairs.map(pair => (
                        <option key={pair.legacyId} value={pair.legacyId}>{formatLabel(pair)}</option>
                    ))}
                </select>

                <Pencil
                    onClick={!isReadOnly ? handleEdit : undefined}
                    className={`cursor-pointer ${isReadOnly ? 'opacity-40 cursor-not-allowed pointer-events-none' : ''}`}
                />

                <Plus
                    onClick={!isReadOnly ? handleAdd : undefined}
                    className={`cursor-pointer ${isReadOnly ? 'opacity-40 cursor-not-allowed pointer-events-none' : ''}`}
                />

                {borrowerPairs.length > 1 && (
                    <Trash
                        onClick={!isReadOnly ? handleDelete : undefined}
                        className={`cursor-pointer ${isReadOnly ? 'opacity-40 cursor-not-allowed pointer-events-none' : ''}`}
                    />
                )}



            </div>

            {showEditSection && (
                <FloatingPopup
                    title={editMode ? 'Edit Borrower Pair' : 'Add Borrower Pair'}
                    onClose={() => setShowEditSection(false)}
                >
                    <AddEditBorrowerPairSection
                        isVisible={true}
                        editMode={editMode}
                        pairData={pairToEdit}
                        token={token}
                        loanid={loanid}
                        onCancel={() => setShowEditSection(false)}
                        onSuccess={(retainLegacyId) => {
                            loadPairs(retainLegacyId);
                            setToastText(editMode ? 'Borrower pair updated' : 'Borrower pair added');
                            showToast(true);

                            setTimeout(() => setShowEditSection(false), 300);
                        }}

                        showWarningToast={showWarningToast}
                        setWarningMessage={setWarningMessage}
                        setLoading={setLoading}
                    />
                </FloatingPopup>
            )}

            <ConfirmDeleteBorrowerModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                borrowerPairId={selectedPairId}
                token={token}
                loanid={loanid}
                onDeleted={() => {
                    const index = borrowerPairs.findIndex(p => p.legacyId === selectedPairId);
                    const nextId = borrowerPairs[index + 1]?.legacyId || borrowerPairs[index - 1]?.legacyId || '';
                    loadPairs(nextId);
                    setToastText("Borrower pair deleted");
                    showToast(true);
                    setTimeout(() => setShowDeleteModal(false), 200);
                }}
            />

        </div>
    );
});

export default BorrowerPairSection;
